import {Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import {Company} from "../../../interfaces/company.interface";
import {AsyncPipe, JsonPipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {AssistantPromptComponent} from "../../../common/assistant-prompt/assistant-prompt.component";
import * as SearchActions from '../../../state/search/search.actions';
import * as SearchSelectors from '../../../state/search/search.selectors';
import {takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {combineLatest, Observable, Subject} from "rxjs";
import {EventTypes} from "../../../event-types";
import {SharedService} from "../../../services/shared.service";
import {PromptService} from "../../../services/prompt.service";
import {PopupService} from "../../../services/popup.service";
import {AuthService} from "../../../services/auth.service";
import {SqlQualificationService, SqlState, sqlIsRunning, sqlTierLabel} from "../../../services/sql-qualification.service";
import {Subscription} from "rxjs";

interface AgentTool {
  id: string;
  title: string;
  description: string;
  credits: number;
  insightType: string;
  enabled: boolean;
  tooltipText?: string;
  assistantTag: string;
  insightKey: string;
}

interface ToolState {
  isGenerated: boolean;
  isAvailable: boolean;
  buttonText: string;
  isDisabled: boolean;
}

@Component({
  selector: 'app-ai-agent-tool-box',
  standalone: true,
  imports: [
    NgIf,
    MatTooltip,
    AssistantPromptComponent,
    JsonPipe,
    AsyncPipe,
    NgClass,
    NgForOf
  ],
  templateUrl: './ai-agent-tool-box.component.html',
  styleUrl: './ai-agent-tool-box.component.scss'
})
export class AiAgentToolBoxComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() reportSearch: any;
  @Input() company: any;
  @Output() enrichRequested = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  loading$: Observable<boolean>;
  error$: Observable<any>;
  insights$: Observable<any>;
  currentInsightType$: Observable<string | null>;

  // Store the computed values to avoid repeated calculations
  showSpecialBox: boolean = false;
  isGeneratingSpecialBox: boolean = false;
  showEmailSequenceBox: boolean = false;
  isGeneratingEmailSequence: boolean = false;
  showLinkedInSequenceBox: boolean = false;
  isGeneratingLinkedInSequence: boolean = false;
  isEnriching: boolean = false;

  // Cache for tool states to avoid repeated calculations
  private toolStatesCache: Map<string, ToolState> = new Map();

  // User data for personalization
  user: any = null;

  // Sales Qualify tile state (shared with the SQL card and the wizard)
  sqlState: SqlState = {enabled: null, qualification: null, creditCost: 3, loaded: false, loading: false, starting: false, saving: false};
  private sqlSub: Subscription | null = null;
  private sqlCompanyId: number | null = null;

  get sqlIsRunning(): boolean {
    return sqlIsRunning(this.sqlState.qualification);
  }

  get sqlStatusLabel(): string {
    const q = this.sqlState.qualification;
    if (!q || q.status === 'failed') {
      return `${this.sqlState.creditCost} credits`;
    }
    if (this.sqlIsRunning) {
      if (q.stop_requested_at) {
        return 'Stopping · finishing with what was found';
      }
      const current = q.progress?.steps?.find(s => s.status === 'running' || s.status === 'waiting');
      return current ? `In progress · ${current.label}` : 'In progress';
    }
    if (q.status === 'needs_input') {
      const n = q.pending_questions?.length ?? 0;
      return `Score ${q.score} · ${n} question${n === 1 ? '' : 's'} need you`;
    }
    return `SQL ${q.score} · ${sqlTierLabel(q)}`;
  }

  get sqlButtonText(): string {
    const q = this.sqlState.qualification;
    if (!q || q.status === 'failed') {
      return 'Sales Qualify';
    }
    if (this.sqlIsRunning) {
      return 'Follow progress';
    }
    return q.status === 'needs_input' ? 'Continue' : 'Open';
  }

  openSalesQualify(): void {
    if (this.reportSearch?.id && this.company?.id) {
      this.sqlService.requestOpen(Number(this.reportSearch.id), Number(this.company.id));
    }
  }

  private bindSqlState(): void {
    const companyId = Number(this.company?.id);
    if (!companyId || companyId === this.sqlCompanyId) {
      return;
    }
    this.sqlCompanyId = companyId;
    this.sqlSub?.unsubscribe();
    this.sqlSub = this.sqlService.state$(companyId).subscribe(state => {
      this.sqlState = state;
      this.cdr.markForCheck();
    });

    // The tool box asks for the feature state itself (allowlist + any existing
    // result) instead of relying on the header card to have loaded it.
    const searchId = Number(this.reportSearch?.id);
    const snapshot = this.sqlService.snapshot(companyId);
    if (searchId && !snapshot.loaded && !snapshot.loading) {
      this.sqlService.load(searchId, companyId);
    }
  }

  // Computed company object with insights
  get companyWithInsights(): any {
    if (!this.company) return null;

    // Merge company with insights from reportSearch
    return {
      ...this.company,
      insights: this.reportSearch?.insights || this.company.insights
    };
  }

  agentTools: AgentTool[] = [
    {
      id: 'startup-investors',
      title: 'Startup Investors',
      description: 'Tailored insights for startup fundraising',
      credits: 2,
      insightType: 'special_box',
      enabled: true,
      assistantTag: 'ASK_ASSISTANT_SPECIAL_BOX',
      insightKey: 'special_box'
    },
    {
      id: 'email-sequence',
      title: 'Email Sequence',
      description: 'AI-crafted 3-email sequence with personalized subject lines and content',
      credits: 2,
      insightType: 'email_sequence',
      enabled: true,
      assistantTag: 'ASK_ASSISTANT_EMAIL_SEQUENCE',
      insightKey: 'email_sequence'
    },
    {
      id: 'linkedin-sequence',
      title: 'LinkedIn Sequence',
      description: 'AI-generated 5-message LinkedIn outreach sequence for social selling',
      credits: 2,
      insightType: 'linkedin_sequence',
      enabled: true,
      assistantTag: 'ASK_ASSISTANT_LINKEDIN_SEQUENCE',
      insightKey: 'linkedin_sequence'
    }
  ];

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef,
    private promptService: PromptService,
    private popupService: PopupService,
    private authService: AuthService,
    private sqlService: SqlQualificationService
  ) {
    // Define global copy functions immediately
    this.setupGlobalCopyFunctions();
    this.loading$ = this.store.select(SearchSelectors.selectInsightsLoading);
    this.error$ = this.store.select(SearchSelectors.selectInsightsError);
    this.insights$ = this.store.select(SearchSelectors.selectInsightsData);
    this.currentInsightType$ = this.store.select(SearchSelectors.selectCurrentInsightType);
  }

  ngOnInit(): void {
    this.bindSqlState();

    // Note: reportSearch may not be available yet in ngOnInit (Inputs are set after)
    // The actual initialization will happen in ngAfterViewInit and ngOnChanges

    // Calculate the visibility once on init - this happens on page load
    this.updateBoxVisibility();

    // Initialize tool states
    this.updateToolStates();

    // Subscribe to prompt updates with report ID filtering
    this.promptService.promptUpdate$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(({ tag, content, reportId }) => {
      // Only process events for THIS report (compare as numbers to avoid type mismatch)
      if (Number(reportId) !== Number(this.reportSearch?.id)) {
        return; // Silently ignore events for other reports
      }

      if (tag === 'ASK_ASSISTANT_SPECIAL_BOX') {
        // console.log('Special box prompt update received', { tag, content });

        // Clear the generating flag
        if (this.isGeneratingSpecialBox) {
          // console.log('Clearing isGeneratingSpecialBox flag from prompt update');
          this.isGeneratingSpecialBox = false;
        }

        // Force update the visibility and trigger change detection
        this.updateBoxVisibility();
        this.updateToolStates();
        this.cdr.detectChanges();
      }

      if (tag === 'ASK_ASSISTANT_EMAIL_SEQUENCE') {
        console.log('Email sequence prompt update received', { tag, content });

        // Clear the generating flag
        if (this.isGeneratingEmailSequence) {
          this.isGeneratingEmailSequence = false;
        }

        // Force update the visibility and trigger change detection
        this.updateBoxVisibility();
        this.updateToolStates();
        this.cdr.detectChanges();
      }

      if (tag === 'ASK_ASSISTANT_LINKEDIN_SEQUENCE') {
        console.log('LinkedIn sequence prompt update received', { tag, content });

        // Clear the generating flag
        if (this.isGeneratingLinkedInSequence) {
          this.isGeneratingLinkedInSequence = false;
        }

        // Force update the visibility and trigger change detection
        this.updateBoxVisibility();
        this.updateToolStates();
        this.cdr.detectChanges();
      }
    });

    // Subscribe to insights to handle successful generation
    this.insights$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(insights => {
      if (insights) {
        // console.log('Insights received:', insights);
        // Update visibility when new insights are received
        this.updateBoxVisibility();
        this.updateToolStates();
      }
    });

    // Subscribe to loading state changes
    combineLatest([this.loading$, this.currentInsightType$]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([loading, currentType]) => {
      // console.log('Loading state changed:', { loading, currentType, isGenerating: this.isGeneratingSpecialBox });

      // Only update visibility if we're not in the middle of generating
      if (!loading && (currentType === 'special_box' || currentType === 'email_sequence' || currentType === 'linkedin_sequence')) {
        // Use a small delay to ensure the status has been updated
        setTimeout(() => {
          this.updateBoxVisibility();
          this.updateToolStates();
        }, 500);
      }
    });

    // Subscribe to errors
    this.error$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(error => {
      if (error) {
        console.error('Error generating insights:', error);
        // Clear generating flag on error
        this.isGeneratingSpecialBox = false;
        this.isGeneratingEmailSequence = false;
        this.isGeneratingLinkedInSequence = false;
        this.updateBoxVisibility();
        this.updateToolStates();
      }
    });

    // Subscribe to user authentication for personalization
    this.authService.authUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });
  }

  ngOnChanges(changes: any): void {
    if (changes.company || changes.reportSearch) {
      if (changes.reportSearch && !changes.company) {
        this.sqlCompanyId = null; // re-bind so the first load fires once the report id is known
      }
      this.bindSqlState();
    }
    // Update visibility if company or reportSearch changes
    if (changes.reportSearch && !changes.reportSearch.firstChange) {
      // Clear enriching flag when enrichment completes
      if (changes.reportSearch.currentValue?.is_enrich === 1 && this.isEnriching) {
        this.isEnriching = false;
      }

      // Check if the special_box status changed to 'finished' or generated became true
      const currSpecialStatus = changes.reportSearch.currentValue?.insights?.special_box?.status;
      const currSpecialGenerated = changes.reportSearch.currentValue?.insights?.special_box?.generated;

      // Check if the email_sequence status changed to 'finished' or generated became true
      const currEmailStatus = changes.reportSearch.currentValue?.insights?.email_sequence?.status;
      const currEmailGenerated = changes.reportSearch.currentValue?.insights?.email_sequence?.generated;

      // Check if the linkedin_sequence status changed to 'finished' or generated became true
      const currLinkedInStatus = changes.reportSearch.currentValue?.insights?.linkedin_sequence?.status;
      const currLinkedInGenerated = changes.reportSearch.currentValue?.insights?.linkedin_sequence?.generated;

      // Clear generating flags when insights are finished
      if ((currSpecialStatus === 'finished' || currSpecialGenerated === true) && this.isGeneratingSpecialBox) {
        console.log('[AI Agent ToolBox] Special box generation completed');
        this.isGeneratingSpecialBox = false;
        this.cdr.detectChanges();
      }

      if ((currEmailStatus === 'finished' || currEmailGenerated === true) && this.isGeneratingEmailSequence) {
        console.log('[AI Agent ToolBox] Email sequence generation completed');
        this.isGeneratingEmailSequence = false;
        this.cdr.detectChanges();
      }

      if ((currLinkedInStatus === 'finished' || currLinkedInGenerated === true) && this.isGeneratingLinkedInSequence) {
        console.log('[AI Agent ToolBox] LinkedIn sequence generation completed');
        this.isGeneratingLinkedInSequence = false;
        this.cdr.detectChanges();
      }

      this.updateBoxVisibility();
      this.updateToolStates();
    }

    if (changes.company) {
      // Check company insights if they exist
      const currSpecialStatus = changes.company?.currentValue?.insights?.special_box?.status;
      const currSpecialGenerated = changes.company?.currentValue?.insights?.special_box?.generated;
      const currEmailStatus = changes.company?.currentValue?.insights?.email_sequence?.status;
      const currEmailGenerated = changes.company?.currentValue?.insights?.email_sequence?.generated;
      const currLinkedInStatus = changes.company?.currentValue?.insights?.linkedin_sequence?.status;
      const currLinkedInGenerated = changes.company?.currentValue?.insights?.linkedin_sequence?.generated;

      // Clear generating flags from company changes
      if ((currSpecialStatus === 'finished' || currSpecialGenerated === true) && this.isGeneratingSpecialBox) {
        console.log('[AI Agent ToolBox] Special box generation completed (from company)');
        this.isGeneratingSpecialBox = false;
        this.cdr.detectChanges();
      }

      if ((currEmailStatus === 'finished' || currEmailGenerated === true) && this.isGeneratingEmailSequence) {
        console.log('[AI Agent ToolBox] Email sequence generation completed (from company)');
        this.isGeneratingEmailSequence = false;
        this.cdr.detectChanges();
      }

      if ((currLinkedInStatus === 'finished' || currLinkedInGenerated === true) && this.isGeneratingLinkedInSequence) {
        console.log('[AI Agent ToolBox] LinkedIn sequence generation completed (from company)');
        this.isGeneratingLinkedInSequence = false;
        this.cdr.detectChanges();
      }

      this.updateBoxVisibility();
      this.updateToolStates();
    }
  }

  ngAfterViewInit(): void {
    // Validate that we have the required reportSearch data
    if (!this.reportSearch?.id) {
      console.warn('[AI Agent ToolBox] reportSearch.id not available yet, will initialize on ngOnChanges');
    }

    // Double-check visibility after view initialization
    // This ensures we catch any late-loading data
    setTimeout(() => {
      this.updateBoxVisibility();
      this.updateToolStates();
    }, 0);
  }

  /**
   * Update the visibility of insight boxes
   */
  private updateBoxVisibility(): void {
    // Hide all boxes since all content now shows in modals
    this.showSpecialBox = false;
    this.showEmailSequenceBox = false;
    this.showLinkedInSequenceBox = false;
  }

  /**
   * Update tool states cache to avoid repeated calculations in template
   */
  private updateToolStates(): void {
    // console.log('[updateToolStates] Updating tool states cache');

    this.agentTools.forEach(tool => {
      const insights = this.currentInsights;
      const insight = insights?.[tool.insightKey];

      // Calculate isGenerated
      const isGenerated = insight?.status === 'finished' ||
        (insight?.generated === true && insight?.generated_at !== null);

      // Calculate isAvailable
      const isAvailable = !insights || !insight || insight.available !== false;

      // Calculate button text
      let buttonText = 'Use this agent';
      if (!isAvailable) {
        buttonText = 'Not available';
      } else if (tool.insightKey === 'special_box' && this.isGeneratingSpecialBox) {
        buttonText = 'Generating...';
      } else if (tool.insightKey === 'email_sequence' && this.isGeneratingEmailSequence) {
        buttonText = 'Generating...';
      } else if (tool.insightKey === 'linkedin_sequence' && this.isGeneratingLinkedInSequence) {
        buttonText = 'Generating...';
      } else if (insight?.status === 'in_progress') {
        buttonText = 'Generating...';
      } else if (insight?.status === 'finished') {
        buttonText = 'Already generated';
      }

      // Calculate isDisabled
      const isDisabled = !tool.enabled ||
        !isAvailable ||
        (tool.insightKey === 'special_box' && this.isGeneratingSpecialBox) ||
        (tool.insightKey === 'email_sequence' && this.isGeneratingEmailSequence) ||
        (tool.insightKey === 'linkedin_sequence' && this.isGeneratingLinkedInSequence) ||
        insight?.status === 'in_progress' ||
        insight?.status === 'finished';

      // Store in cache
      this.toolStatesCache.set(tool.insightKey, {
        isGenerated,
        isAvailable,
        buttonText,
        isDisabled
      });
    });

    // console.log('[updateToolStates] Cache updated for', this.toolStatesCache.size, 'tools');
  }

  /**
   * Get cached tool state
   */
  getToolState(tool: AgentTool): ToolState {
    return this.toolStatesCache.get(tool.insightKey) || {
      isGenerated: false,
      isAvailable: true,
      buttonText: 'Use this agent',
      isDisabled: false
    };
  }

  // Computed property to get insights from either reportSearch or company
  get currentInsights(): any {
    // First check reportSearch.insights
    if (this.reportSearch?.insights) {
      return this.reportSearch.insights;
    }

    // Then check if company has insights
    if (this.company?.insights) {
      return this.company.insights;
    }

    // Finally, check if reportSearch has the current company's insights
    if (this.reportSearch?.search_companies && this.company) {
      const currentCompany = this.reportSearch.search_companies.find(
        (c: any) => c.id === this.company.id
      );
      return currentCompany?.insights || null;
    }

    return null;
  }

  /**
   * Check if an insight is available (can be generated)
   */
  isInsightAvailable(tool: AgentTool): boolean {
    return this.getToolState(tool).isAvailable;
  }

  /**
   * Check if an insight has been generated (status is 'finished')
   */
  isInsightGenerated(tool: AgentTool): boolean {
    return this.getToolState(tool).isGenerated;
  }

  /**
   * Check if report has an assistant prompt with given tag (legacy support)
   */
  hasAssistantPrompt(tag: string): boolean {
    if (!this.reportSearch?.assistant_prompts || !Array.isArray(this.reportSearch.assistant_prompts)) {
      return false;
    }

    return this.reportSearch.assistant_prompts.some((prompt: any) => prompt.tag === tag);
  }

  /**
   * Check if a specific tool is currently generating
   */
  isToolGenerating(tool: AgentTool): boolean {
    return (tool.insightKey === 'special_box' && this.isGeneratingSpecialBox) ||
           (tool.insightKey === 'email_sequence' && this.isGeneratingEmailSequence) ||
           (tool.insightKey === 'linkedin_sequence' && this.isGeneratingLinkedInSequence);
  }

  /**
   * View the content of a generated insight
   */
  viewContent(tool: AgentTool): void {
    if (!tool || !tool.insightKey) {
      return;
    }

    // Show the corresponding box section
    if (tool.insightKey === 'special_box') {
      // Open special box modal directly instead of showing blue card
      const insights = this.currentInsights;
      const insight = insights?.[tool.insightKey];

      if (insight?.response) {
        this.openSpecialBoxModal(insight.response);
      }
    } else if (tool.insightKey === 'email_sequence') {
      // Instead of scrolling to blue card, open modal directly
      const insights = this.currentInsights;
      const insight = insights?.[tool.insightKey];

      if (insight?.response) {
        this.openEmailSequenceModal(insight.response);
      }
    } else if (tool.insightKey === 'linkedin_sequence') {
      // Open LinkedIn sequence modal directly
      const insights = this.currentInsights;
      const insight = insights?.[tool.insightKey];

      if (insight?.response) {
        this.openLinkedInSequenceModal(insight.response);
      }
    }
  }

  /**
   * Set up global copy functions for modals
   */
  private setupGlobalCopyFunctions(): void {
    // Define email copy function globally
    (window as any).copyEmailToClipboard = (subject: string, content: string) => {
      // Strip HTML tags from both subject and content
      const cleanSubject = this.stripHtmlTags(subject);
      const cleanContent = this.stripHtmlTags(content);
      const fullEmail = `Subject: ${cleanSubject}\n\n${cleanContent}`;
      this.copyToClipboard(fullEmail, 'Email copied to clipboard!', 'Email content');
    };

    // Define LinkedIn copy function globally
    (window as any).copyLinkedInToClipboard = (content: string) => {
      // Strip HTML tags from content
      const cleanContent = this.stripHtmlTags(content);
      this.copyToClipboard(cleanContent, 'LinkedIn message copied to clipboard!', 'LinkedIn message');
    };
  }

  /**
   * Strip HTML tags from text content
   */
  private stripHtmlTags(html: string): string {
    if (!html) return '';
    // Replace HTML tags and decode common HTML entities
    return html.replace(/<[^>]*>/g, '')
               .replace(/&nbsp;/g, ' ')
               .replace(/&amp;/g, '&')
               .replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>')
               .replace(/&quot;/g, '"')
               .replace(/&#39;/g, "'")
               .trim();
  }

  /**
   * Copy content to clipboard with fallback support
   */
  private copyToClipboard(content: string, successMessage: string, contentType: string = 'Content'): void {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(content).then(() => {
        this.popupService.notify(successMessage, contentType, 'success', 2500);
      }).catch((err) => {
        console.error('Failed to copy:', err);
        this.popupService.notify('Failed to copy to clipboard', 'Copy Error', 'error', 3000);
      });
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = content;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this.popupService.notify(successMessage, contentType, 'success', 2500);
      } catch (err) {
        console.error('Failed to copy:', err);
        this.popupService.notify('Failed to copy to clipboard', 'Copy Error', 'error', 3000);
      }
      document.body.removeChild(textarea);
    }
  }

  /**
   * Replace [Your Name] placeholder with actual user name
   */
  private personalizeContent(content: string): string {
    if (!content || typeof content !== 'string') {
      return content;
    }

    const userName = this.user?.full_name || this.user?.name || this.user?.first_name || '[Your Name]';
    return content.replace(/\[Your Name\]/g, userName);
  }

  /**
   * Open email sequence modal
   */
  private openEmailSequenceModal(emailData: any): void {
    console.log('Opening email sequence modal with data:', emailData);
    console.log('Data type:', typeof emailData);
    console.log('Is string?', typeof emailData === 'string');

    // Parse the data if it's a string
    let parsedData = emailData;
    if (typeof emailData === 'string') {
      try {
        parsedData = JSON.parse(emailData);
        console.log('Parsed data:', parsedData);
      } catch (e) {
        console.error('Error parsing email data:', e);
        parsedData = emailData;
      }
    }

    // Format the email data for display in modal
    let modalContent = '';

    if (parsedData.subject && parsedData.content) {
      // Single email format
      modalContent = `
        <div class="email-modal-content">
          <div class="bg-white border border-gray-200 rounded-lg p-6 mb-4">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-md font-semibold text-gray-900">Email 1</h4>
              <span class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Single Email</span>
            </div>
            <div class="mb-3">
              <p class="text-sm text-gray-600 mb-1"><strong>Subject:</strong></p>
              <p class="text-sm font-medium text-gray-900">${parsedData.subject}</p>
            </div>
            <div class="text-sm text-gray-700 leading-relaxed">${parsedData.content}</div>
            ${parsedData.description ? `<div class="mt-4 pt-4 border-t border-gray-100"><p class="text-xs text-gray-500 italic">${parsedData.description}</p></div>` : ''}
          </div>
        </div>
      `;
    } else if (parsedData.emails && Array.isArray(parsedData.emails)) {
      // Multiple emails format (3-email sequence)
      console.log('Processing emails array:', parsedData.emails);
      modalContent = '<div class="email-modal-content grid grid-cols-3 gap-6">';
      parsedData.emails.forEach((email: any, index: number) => {
        const emailContent = this.personalizeContent(email.content || email.body || 'No content');
        const emailSubject = this.personalizeContent(email.subject || 'No subject');

        modalContent += `
          <div class="bg-white border border-gray-200 rounded-lg p-6 relative">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-md font-semibold text-gray-900">Email ${index + 1}</h4>
              <div class="flex items-center gap-2">
                <span class="text-xs text-blue-royal font-medium bg-blue-royal/10 px-2 py-0.5 rounded-full">Sequence ${index + 1}/3</span>
                <button type="button" onclick="window.copyEmailToClipboard('${emailSubject.replace(/'/g, "\\'")}', '${emailContent.replace(/'/g, "\\'").replace(/\n/g, "\\n")}')" class="text-blue-royal hover:opacity-90 transition-transform duration-300 hover:-rotate-6" title="Copy email">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
                    <path fill-rule="evenodd" d="M13.887 3.182c.396.037.79.08 1.183.128C16.194 3.45 17 4.414 17 5.517V16.75A2.25 2.25 0 0 1 14.75 19h-9.5A2.25 2.25 0 0 1 3 16.75V5.517c0-1.103.806-2.068 1.93-2.207.393-.048.787-.09 1.183-.128A3.001 3.001 0 0 1 9 1h2c1.373 0 2.531.923 2.887 2.182ZM7.5 4A1.5 1.5 0 0 1 9 2.5h2A1.5 1.5 0 0 1 12.5 4v.5h-5V4Z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="mb-4">
              <p class="text-sm text-gray-600 mb-2"><strong>Subject:</strong> ${emailSubject}</p>
            </div>
            <div class="text-sm text-gray-700 leading-relaxed">${emailContent}</div>
          </div>
        `;
      });
      modalContent += '</div>';
    } else {
      // Fallback format
      modalContent = `
        <div class="email-modal-content">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 class="text-md font-semibold text-gray-900 mb-4">Email Sequence Data</h4>
            <pre class="text-xs text-gray-700 whitespace-pre-wrap">${JSON.stringify(emailData, null, 2)}</pre>
          </div>
        </div>
      `;
    }


    // Use infoWide for better display of email sequences
    this.popupService.infoWide('Generate Email Sequence Insight', modalContent);
  }

  /**
   * Open LinkedIn sequence modal
   */
  private openLinkedInSequenceModal(linkedinData: any): void {
    console.log('Opening LinkedIn sequence modal with data:', linkedinData);
    console.log('Data type:', typeof linkedinData);
    console.log('Is string?', typeof linkedinData === 'string');

    // Parse the data if it's a string
    let parsedData = linkedinData;
    if (typeof linkedinData === 'string') {
      try {
        parsedData = JSON.parse(linkedinData);
        console.log('Parsed data:', parsedData);
      } catch (e) {
        console.error('Error parsing LinkedIn data:', e);
        parsedData = linkedinData;
      }
    }

    // Format the LinkedIn data for display in modal
    let modalContent = '';

    if (parsedData.messages && Array.isArray(parsedData.messages)) {
      // LinkedIn messages format (5-message sequence)
      console.log('Processing LinkedIn messages array:', parsedData.messages);
      modalContent = '<div class="linkedin-modal-content grid grid-cols-5 gap-4">';
      parsedData.messages.forEach((message: any, index: number) => {
        const messageContent = this.personalizeContent(message.content || message.message || 'No content');
        const messageType = message.type || `Message ${index + 1}`;

        modalContent += `
          <div class="bg-white border border-gray-200 rounded-lg p-4 relative">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-gray-900">${messageType}</h4>
              <div class="flex items-center gap-2">
                <span class="text-xs text-blue-royal bg-blue-royal/10 font-medium px-2 py-0.6 rounded-full">${index + 1}/5</span>
                <button type="button" onclick="window.copyLinkedInToClipboard('${messageContent.replace(/'/g, "\\'").replace(/\n/g, "\\n")}')" class="text-blue-royal hover:opacity-90 transition-transform duration-300 hover:-rotate-6" title="Copy message">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
                    <path fill-rule="evenodd" d="M13.887 3.182c.396.037.79.08 1.183.128C16.194 3.45 17 4.414 17 5.517V16.75A2.25 2.25 0 0 1 14.75 19h-9.5A2.25 2.25 0 0 1 3 16.75V5.517c0-1.103.806-2.068 1.93-2.207.393-.048.787-.09 1.183-.128A3.001 3.001 0 0 1 9 1h2c1.373 0 2.531.923 2.887 2.182ZM7.5 4A1.5 1.5 0 0 1 9 2.5h2A1.5 1.5 0 0 1 12.5 4v.5h-5V4Z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="text-sm text-gray-700 leading-relaxed">${messageContent}</div>
          </div>
        `;
      });
      modalContent += '</div>';
    } else if (Array.isArray(parsedData)) {
      // Direct array of LinkedIn messages
      console.log('Processing direct LinkedIn messages array:', parsedData);
      modalContent = '<div class="linkedin-modal-content grid grid-cols-5 gap-4">';
      parsedData.forEach((message: any, index: number) => {
        const messageContent = this.personalizeContent(message.content || message.message || 'No content');
        const messageType = message.type || `Message ${index + 1}`;

        modalContent += `
          <div class="bg-white border border-gray-200 rounded-lg p-4 relative">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-gray-900">${messageType}</h4>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">${index + 1}/5</span>
                <button type="button" onclick="window.copyLinkedInToClipboard('${messageContent.replace(/'/g, "\\'").replace(/\n/g, "\\n")}')" class="text-blue-royal hover:opacity-90 transition-transform duration-300 hover:-rotate-6" title="Copy message">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
                    <path fill-rule="evenodd" d="M13.887 3.182c.396.037.79.08 1.183.128C16.194 3.45 17 4.414 17 5.517V16.75A2.25 2.25 0 0 1 14.75 19h-9.5A2.25 2.25 0 0 1 3 16.75V5.517c0-1.103.806-2.068 1.93-2.207.393-.048.787-.09 1.183-.128A3.001 3.001 0 0 1 9 1h2c1.373 0 2.531.923 2.887 2.182ZM7.5 4A1.5 1.5 0 0 1 9 2.5h2A1.5 1.5 0 0 1 12.5 4v.5h-5V4Z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="text-sm text-gray-700 leading-relaxed">${messageContent}</div>
          </div>
        `;
      });
      modalContent += '</div>';
    } else {
      // Fallback format
      modalContent = `
        <div class="linkedin-modal-content">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 class="text-md font-semibold text-gray-900 mb-4">LinkedIn Sequence Data</h4>
            <pre class="text-xs text-gray-700 whitespace-pre-wrap">${JSON.stringify(linkedinData, null, 2)}</pre>
          </div>
        </div>
      `;
    }


    // Use infoWide for better display of LinkedIn sequences
    this.popupService.infoWide('Generate LinkedIn Sequence Insight', modalContent);
  }

  /**
   * Open special box modal
   */
  private openSpecialBoxModal(specialBoxData: any): void {
    console.log('Opening special box modal with data:', specialBoxData);
    console.log('Data type:', typeof specialBoxData);
    console.log('Is string?', typeof specialBoxData === 'string');

    // Parse the data if it's a string
    let parsedData = specialBoxData;
    if (typeof specialBoxData === 'string') {
      try {
        parsedData = JSON.parse(specialBoxData);
        console.log('Parsed data:', parsedData);
      } catch (e) {
        console.error('Error parsing special box data:', e);
        parsedData = specialBoxData;
      }
    }

    // Extract content from API response structure if present
    let actualContent = parsedData;
    if (parsedData && typeof parsedData === 'object') {
      // Check if it's an API response with message.content structure
      if (parsedData.message && parsedData.message.content) {
        actualContent = parsedData.message.content;
        console.log('Extracted content from message.content:', actualContent);
      }
      // Check if it has a direct content field
      else if (parsedData.content) {
        actualContent = parsedData.content;
        console.log('Extracted content from content field:', actualContent);
      }
    }

    // Parse the actual content if it's a JSON string
    if (typeof actualContent === 'string') {
      try {
        const jsonContent = JSON.parse(actualContent);
        if (typeof jsonContent === 'object') {
          actualContent = jsonContent;
          console.log('Parsed content as JSON:', actualContent);
        }
      } catch (e) {
        // Not valid JSON, keep as string
        console.log('Content is not JSON, keeping as string');
      }
    }

    // Format the special box data for display in modal
    let modalContent = '';

    // Helper function to strip HTML tags and get clean text
    const stripHtmlTags = (html: string): string => {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, '').trim();
    };

    if (actualContent && typeof actualContent === 'object' && actualContent.current_stage_of_the_fund) {
      // Structured special box data format
      const cleanShort = stripHtmlTags(actualContent.short);
      modalContent = `
        <div class="special-box-modal-content">
          <div>
            <div class="space-y-4">
              ${(cleanShort && cleanShort.length > 0) ? `<div class="blue-card p-3 mb-6"><h4 class="text-sm font-semibold text-secondary-900 mb-2">Summary</h4><p class="text-sm text-blue-800 leading-relaxed">${cleanShort}</p></div>` : ''}
              ${(() => {
                const cleanSummary = stripHtmlTags(actualContent.summary);
                return cleanSummary && cleanSummary.length > 0 ? `<div class="mb-6"><p class="text-sm text-gray-700 leading-relaxed">${cleanSummary}</p></div>` : '';
              })()}
              ${(() => {
                const cleanFields = {
                  current_stage_of_the_fund: stripHtmlTags(actualContent.current_stage_of_the_fund),
                  investment_stages: stripHtmlTags(actualContent.investment_stages),
                  summary_investment_thesis: stripHtmlTags(actualContent.summary_investment_thesis),
                  ticket_size: stripHtmlTags(actualContent.ticket_size),
                  top_startups: stripHtmlTags(actualContent.top_startups)
                };

                const hasAnyField = Object.values(cleanFields).some(field => field && field.length > 0);

                if (!hasAnyField) return '';

                return `<ul class="space-y-3">
                  ${cleanFields.current_stage_of_the_fund && cleanFields.current_stage_of_the_fund.length > 0 ? `<li class="list-none"><span class="font-medium text-gray-900 mr-2">Current stage of the fund:</span><span class="text-gray-700">${cleanFields.current_stage_of_the_fund}</span></li>` : ''}
                  ${cleanFields.investment_stages && cleanFields.investment_stages.length > 0 ? `<li class="list-none"><span class="font-medium text-gray-900 mr-2">Investment stages:</span><span class="text-gray-700">${cleanFields.investment_stages}</span></li>` : ''}
                  ${cleanFields.summary_investment_thesis && cleanFields.summary_investment_thesis.length > 0 ? `<li class="list-none"><span class="font-medium text-gray-900 mr-2">Summary investment thesis:</span><span class="text-gray-700">${cleanFields.summary_investment_thesis}</span></li>` : ''}
                  ${cleanFields.ticket_size && cleanFields.ticket_size.length > 0 ? `<li class="list-none"><span class="font-medium text-gray-900 mr-2">Ticket size:</span><span class="text-gray-700">${cleanFields.ticket_size}</span></li>` : ''}
                  ${cleanFields.top_startups && cleanFields.top_startups.length > 0 ? `<li class="list-none"><span class="font-medium text-gray-900 mr-2">Top startups:</span><span class="text-gray-700">${cleanFields.top_startups}</span></li>` : ''}
                </ul>`;
              })()}
            </div>
          </div>
        </div>
      `;
    } else if (typeof actualContent === 'string' && actualContent.trim() !== '') {
      // Plain text format (non-empty string)
      modalContent = `
        <div class="special-box-modal-content">
          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">${actualContent}</div>
          </div>
        </div>
      `;
    } else if (typeof actualContent === 'string' && actualContent.trim() === '') {
      // Empty content
      modalContent = `
        <div class="special-box-modal-content">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <p class="text-sm text-gray-700 text-center italic">No content available</p>
          </div>
        </div>
      `;
    } else {
      // Fallback format - show the raw data
      modalContent = `
        <div class="special-box-modal-content">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 class="text-md font-semibold text-gray-900 mb-4">Special Box Data</h4>
            <div class="text-sm text-gray-700">
              <p class="mb-2"><strong>Raw Response:</strong></p>
              <pre class="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border">${JSON.stringify(specialBoxData, null, 2)}</pre>
              ${actualContent !== specialBoxData ? `
                <p class="mb-2 mt-4"><strong>Extracted Content:</strong></p>
                <pre class="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border">${JSON.stringify(actualContent, null, 2)}</pre>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }

    // Use info modal for special box content
    this.popupService.info('Special box for startups looking for investors', modalContent);
  }


  /**
   * Check if the button should be disabled
   */
  isButtonDisabled(tool: AgentTool, loading: boolean): boolean {
    return this.getToolState(tool).isDisabled || loading;
  }

  /**
   * Get button text based on insight status
   */
  getButtonText(tool: AgentTool): string {
    return this.getToolState(tool).buttonText;
  }

  useAgent(tool: AgentTool): void {
    const insight = this.reportSearch?.insights?.[tool.insightKey];

    // Don't allow if already in progress or finished
    if (!tool || !tool.enabled || !this.isInsightAvailable(tool) ||
      insight?.status === 'in_progress' || insight?.status === 'finished' ||
      (tool.insightKey === 'special_box' && this.isGeneratingSpecialBox) ||
      (tool.insightKey === 'email_sequence' && this.isGeneratingEmailSequence) ||
      (tool.insightKey === 'linkedin_sequence' && this.isGeneratingLinkedInSequence)) {
      console.log('Cannot use agent:', {
        enabled: tool?.enabled,
        available: this.isInsightAvailable(tool),
        status: insight?.status,
        isGeneratingSpecial: this.isGeneratingSpecialBox,
        isGeneratingEmail: this.isGeneratingEmailSequence,
        isGeneratingLinkedIn: this.isGeneratingLinkedInSequence
      });
      return;
    }

    if (!this.reportSearch?.id) {
      console.error('Cannot generate insights without a report ID');
      return;
    }

    // console.log('Using agent:', tool.title);
    // console.log('Report ID:', this.reportSearch.id);
    // console.log('Insight Type:', tool.insightType);
    // console.log('Assistant Tag:', tool.assistantTag);

    // If this is the special box tool, set generating flag but don't show blue box
    if (tool.insightKey === 'special_box') {
      this.isGeneratingSpecialBox = true;
      console.log('Set isGeneratingSpecialBox to true');
    }

    // If this is the email sequence tool, set generating flag but don't show blue box
    if (tool.insightKey === 'email_sequence') {
      this.isGeneratingEmailSequence = true;
      console.log('Set isGeneratingEmailSequence to true');
    }

    // If this is the LinkedIn sequence tool, set generating flag but don't show blue box
    if (tool.insightKey === 'linkedin_sequence') {
      this.isGeneratingLinkedInSequence = true;
      console.log('Set isGeneratingLinkedInSequence to true');
    }

    // Update tool states immediately
    this.updateToolStates();

    // Dispatch the action to generate insights
    this.store.dispatch(SearchActions.generateInsights({
      searchId: this.reportSearch.id,
      insightType: tool.insightType
    }));

    // Force change detection to update the button immediately
    this.cdr.detectChanges();
  }

  onEnrichProfile(): void {
    if (this.reportSearch?.is_enrich === 1 || this.isEnriching) return;
    this.isEnriching = true;
    this.enrichRequested.emit();
  }

  ngOnDestroy(): void {
    this.sqlSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

}
